/**
 * Class description
 *
 * Created: 2017-10-10
 * @author jonas
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Linear, Power2, Timeline, TimelineLite, TweenLite} from "gsap"
import {DungeonQuestLayerConfig} from "../../config/DungeonQuestLayerConfig";
import {DungeonQuestSymbolUtil} from "../../utils/DungeonQuestSymbolUtil";
import {MaskCreator} from "../../utils/MaskCreator";
import {MathHelper} from "../../utils/MathHelper";
import {StateMatrix} from "../../utils/StateMatrix";
import {DungeonQuestGameAssets} from "../DungeonQuestGameAssets";
import {DungeonQuestGameConfig} from "../DungeonQuestGameConfig";
import {DungeonQuestSound} from "../DungeonQuestSound";
import {ColossalSymbolInfo} from "../server/data/DungeonQuestParsedGameData";
import {DungeonQuestHelper} from "./DungeonQuestHelper";
import {WaveConfig} from "./WaveConfig";


export class DungeonQuestSymbolTransformView extends BaseView {

    private _shockWaveRing:PIXI.Sprite;
    private _reelRect:PIXI.Rectangle;
    private _maskGfx:PIXI.Graphics;

    constructor(resourcesGroup?:ResourcesGroupName) {
        super(resourcesGroup);

        const layer = StageManager.getLayer(DungeonQuestLayerConfig.BIG_SYMBOLS.name);
        this.position.set(DungeonQuestGameConfig.instance.REEL_AREA_POS_X, DungeonQuestGameConfig.instance.REEL_AREA_POS_Y);
        layer.addChild(this);

        this._reelRect = new PIXI.Rectangle(
            DungeonQuestGameConfig.instance.REEL_AREA_POS_X + 10,
            DungeonQuestGameConfig.instance.REEL_AREA_POS_Y - 5,
            DungeonQuestGameConfig.instance.SYMBOL_WIDTH,
            DungeonQuestGameConfig.instance.SYMBOL_HEIGHT
        );
    }

    protected initAnimations():void {
        this._shockWaveRing = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.SHOCK_WAVE_RING)[0]);
        this._shockWaveRing.anchor.set(0.5, 0.5);
        this._shockWaveRing.visible = false;
        this.addChild(this._shockWaveRing);
        this.addMask();
    }

    private getColossalScreenSpaceCenter(data:ColossalSymbolInfo):PIXI.Point {
        return new PIXI.Point(
            (data.position[0] + data.size * 0.5) * this._reelRect.width + this._reelRect.x - this._reelRect.width * 0.5,
            (data.position[1] + data.size * 0.5) * this._reelRect.height + this._reelRect.y
        );
    }

    private getColossalViewSpaceCenter(data:ColossalSymbolInfo):PIXI.Point {
        return new PIXI.Point(
            (data.position[0] + data.size * 0.5) * this._reelRect.width,
            (data.position[1] + data.size * 0.5) * this._reelRect.height
        );
    }

    public getLandingShockWave(origin:ColossalSymbolInfo):TimelineLite {
        const waveDistanceMatrix:StateMatrix<number> = DungeonQuestHelper.createSquareDistanceMatrix(origin);
        const waveConfig:WaveConfig = new WaveConfig(0.2, 0.4, 75, 0, WaveConfig.landingForce);

        const waveAnimations:TimelineLite[][] = this.createTransformingWaveAnimations(waveDistanceMatrix, waveConfig, origin, null);

        const tl:TimelineLite = new TimelineLite();
        tl.add(waveAnimations, 0, "start", 0.1);

        return tl;
    }

    public getNewShockWaveTransform(origin:ColossalSymbolInfo, onlyOnPositions:[number, number][] | null):TimelineLite {
        const waveDistanceMatrix:StateMatrix<number> = DungeonQuestHelper.createSquareDistanceMatrix(origin, 1);
        const waveConfig:WaveConfig = new WaveConfig(0.2, 0.5, 0, 1, WaveConfig.transformForce);
        const viewSpaceColossalCenter = this.getColossalViewSpaceCenter(origin);

        Logger.logDev(waveDistanceMatrix.toString());

        if(onlyOnPositions != null) {
            const filterMatrix = new StateMatrix<number>(5, 7, [0, 6], 0);
            filterMatrix.setStatesAt(onlyOnPositions, 1);
            const positionsToIgnore = filterMatrix.getAllCoordinatesWithState(0);
            waveDistanceMatrix.setStatesAt(positionsToIgnore, 0);
        }

        Logger.logDev(waveDistanceMatrix.toString());


        const turnToNormalDuration = 0.5;
        const returnToNormalAnimations:Timeline[] = this.getTurnToNormalAnimations(waveDistanceMatrix.getAllCoordinatesWithState(0), turnToNormalDuration);
        const waveAnimations:TimelineLite[][] = this.createTransformingWaveAnimations(waveDistanceMatrix, waveConfig, origin, "all");
        const tl:TimelineLite = new TimelineLite();
        tl.add(waveAnimations, 0, "start", 0.05);
        tl.add([
            () => SlotGame.sound.playEffect(DungeonQuestSound.TRANSFORM_SHOCK_WAVE),
            this.getRingAnimation(viewSpaceColossalCenter, 0.05)
        ], 0);

        tl.add(returnToNormalAnimations, tl.totalDuration() - turnToNormalDuration, "start", 0);

        return tl;
    }

    private getTurnToNormalAnimations(positions:[number, number][], turnToNormalDuration:number):Timeline[] {
        const tls:Timeline[] = [];

        for(let pos of positions) {
            const symbol = Reels.getSymbol(pos[0], pos[1]);
            if(symbol) {
                const animation = symbol.changeState({
                    state : DungeonQuestGameConfig.instance.SYMBOL_STATES.normal,
                    fadeInDuration : turnToNormalDuration
                });

                if(animation) {
                    tls.push(animation);
                }
            }


        }

        return tls;
    }

    private getRingAnimation(viewSpaceColossalCenter:PIXI.Point, waveOffset:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const size = 2240; // => 140 * 8 * 2;
        const duration = waveOffset * 8;
        tl.add(() => this.resetRingAnimation(viewSpaceColossalCenter));
        tl.add(
            new TweenLite(this._shockWaveRing, duration, {
                width : size,
                height : size,
                ease : Linear.easeNone
            }));

        tl.add(new TweenLite(this._shockWaveRing, 0.1, {
            alpha : 0,
            ease : Linear.easeNone
        }));

        return tl;
    }

    private resetRingAnimation(position:PIXI.Point):void {
        this._shockWaveRing.position.set(position.x, position.y);
        this._shockWaveRing.visible = true;
        this._shockWaveRing.alpha = 1;
        this._shockWaveRing.blendMode = PIXI.BLEND_MODES.ADD;
        this._shockWaveRing.scale.set(1);
    }


    protected onResize(resizeData:IResizeData):void {
        if(resizeData.orientation == Orientation.PORTRAIT) {
            this._maskGfx.scale.x = 2;
        } else {
            this._maskGfx.scale.x = 1;
        }

    }

    private addMask() {
        this._maskGfx = MaskCreator.createSymbolAreaMaskGraphics();
        this._maskGfx.pivot.x = DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5;
        this._maskGfx.position.x = DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5;
        //mask.alpha = 0.5;
        this.addChild(this._maskGfx);
        this.mask = this._maskGfx;
    }

    private createTransformingWaveAnimations(waveDistanceMatrix:StateMatrix<number>, waveConfig:WaveConfig, symbolInfo:ColossalSymbolInfo, symbolsToTransform:string | null) {
        const screenSpaceColossalCenter:PIXI.Point = this.getColossalScreenSpaceCenter(symbolInfo);
        let waveAnimations:TimelineLite[][] = [];
        let states = waveDistanceMatrix.states.concat();
        states.sort((a:number, b:number) => {return a - b});

        let longestDistance:number = states[states.length - 1];

        for(let state of states) {
            let force = waveConfig.forceCalculation(state, longestDistance);
            if(state == 0 || force == 0) {
                continue;
            }

            let positions = waveDistanceMatrix.getAllCoordinatesWithState(state);
            let wave:TimelineLite[] = [];

            for(let position of positions) {
                let symbol:StateReelSymbol | null = Reels.getSymbol(position[0], position[1]);
                if(symbol) {

                    //If transform, only animate the transforming symbols.
                    if(symbolsToTransform != null) {
                        if(symbol.symName != symbolsToTransform && symbolsToTransform != "all") {
                            continue;
                        }
                    }

                    let symWave:TimelineLite = new TimelineLite();
                    let reelOffset = new PIXI.Point(
                        this._reelRect.x + this._reelRect.width * position[0],
                        this._reelRect.y
                    );

                    const startScale = new PIXI.Point(symbol.scale.x, symbol.scale.y);
                    const startPos = new PIXI.Point(0, DungeonQuestGameConfig.instance.SYMBOL_HEIGHT * 0.5 + DungeonQuestGameConfig.instance.SYMBOL_HEIGHT * position[1]);
                    const reelNormalizedPoint = MathHelper.subPoints(screenSpaceColossalCenter, reelOffset);
                    let targetPos = MathHelper.subPoints(startPos, reelNormalizedPoint);
                    targetPos = MathHelper.normalizeVector(targetPos);
                    targetPos = MathHelper.pointMul(targetPos, force * waveConfig.positionMultiplier);
                    targetPos = MathHelper.addPoints(startPos, targetPos);


                    symWave.add([
                        new TweenLite(symbol.scale, waveConfig.spreadDuration, {
                            x : 1 + force * waveConfig.scaleFactor,
                            y : 1 + force * waveConfig.scaleFactor
                        }),
                        new TweenLite(symbol.position, waveConfig.spreadDuration, {
                            x : targetPos.x,
                            y : targetPos.y,
                            ease : Power2.easeOut
                        })
                    ]);
                    symWave.add([
                        new TweenLite(symbol.scale, waveConfig.returnDuration, {x : startScale.x, y : startScale.y}),
                        new TweenLite(symbol.position, waveConfig.returnDuration, {
                            x : startPos.x,
                            y : startPos.y,
                            ease : Power2.easeIn
                        })
                    ]);

                    //Transform animation.
                    if(symbol.symName == symbolsToTransform || symbolsToTransform == "all") {
                        symWave.add(DungeonQuestSymbolUtil.getSymbolReplaceAndTransformAnimation(symbol, symbolInfo.symbol), 0);
                    }

                    wave.push(symWave);

                }
            }
            waveAnimations.push(wave);
        }

        return waveAnimations;
    }

}